import { useCallback, useMemo } from 'react';
import { FormsV1 } from '@frontend/api-digital-forms';
import appConfig from '@frontend/env';
import type { PaymentMethodSetupApi, PaymentMethodSetupGatewayConfig } from '@frontend/payments-platform';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../../../../../constants';
import { useConfigShallowStore } from '../../../../../../stores';
import { useFillingExperienceContext } from '../../../../provider';
import { FetchPaymentMethods, findPaymentMethod, toSavedPaymentMethod } from './payment-field.helpers';

export interface PaymentContext {
  personId: string;
  locationId: string;
  submissionId: string;
}

export const getPaymentContext = (
  personId: string | undefined,
  locationId: string,
  submissionId: string
): PaymentContext | undefined => (personId ? { personId, locationId, submissionId } : undefined);

/** Only solicited links know the patient and submission; undefined elsewhere. */
export const usePaymentContext = (): PaymentContext | undefined => {
  const context = useFillingExperienceContext();
  const { person } = useConfigShallowStore('person');
  const isSolicited = context.experienceType === 'solicited';

  return useMemo(
    () => (isSolicited ? getPaymentContext(person?.id, context.locationId, context.documentId) : undefined),
    [context.documentId, context.locationId, isSolicited, person?.id]
  );
};

export const usePaymentMethodsFetcher = (paymentContext: PaymentContext | undefined): FetchPaymentMethods => {
  const fetchGetPaymentMethods = FormsV1.Queries.useFetchGetPaymentMethods();

  return useCallback(
    async (paymentMethodIds: string[]) => {
      if (!paymentContext) throw new Error('Payment context is not available');

      return fetchGetPaymentMethods({
        request: { ...paymentContext, paymentMethodIds },
        // Polling after confirmation needs a fresh read every time.
        options: { retry: false, staleTime: 0, cacheTime: 0 },
        httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
      });
    },
    [fetchGetPaymentMethods, paymentContext]
  );
};

export const useFormsPaymentMethodSetupApi = (paymentContext: PaymentContext | undefined): PaymentMethodSetupApi => {
  const { mutateAsync } = FormsV1.Mutations.useSetupPaymentMethodMutation({
    httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
  });
  const fetchPaymentMethods = usePaymentMethodsFetcher(paymentContext);

  return useMemo<PaymentMethodSetupApi>(
    () => ({
      createSetupSession: async () => {
        if (!paymentContext) throw new Error('Payment context is not available');

        const response = await mutateAsync(paymentContext);
        if (!response?.clientSecret) throw new Error('Setup session response has no client secret');

        return {
          gateway: 'stripe',
          clientSecret: response.clientSecret,
          connectedAccountId: response.stripeAccountId || undefined,
        };
      },
      getPaymentMethod: async (paymentMethodId, kind) => {
        const method = findPaymentMethod(await fetchPaymentMethods([paymentMethodId]), paymentMethodId);
        return method && toSavedPaymentMethod(method, kind);
      },
    }),
    [fetchPaymentMethods, mutateAsync, paymentContext]
  );
};

/** `@frontend/env`'s types don't include the forms app's `env.d.ts`. */
const paymentEnv = appConfig as typeof appConfig &
  Partial<Record<'STRIPE_CARD_ONLY_PAYMENT_METHOD_CONFIG' | 'STRIPE_ACH_ONLY_PAYMENT_METHOD_CONFIG', string>>;

/** Card-only / ACH-only configs keep other method types and Stripe Link out of each modal. */
export const usePaymentGatewayConfig = (): PaymentMethodSetupGatewayConfig =>
  useMemo(
    () => ({
      stripe: {
        publishableKey: paymentEnv.STRIPE_EXPRESS_API_KEY ?? '',
        paymentMethodConfiguration: {
          card: paymentEnv.STRIPE_CARD_ONLY_PAYMENT_METHOD_CONFIG || undefined,
          ach: paymentEnv.STRIPE_ACH_ONLY_PAYMENT_METHOD_CONFIG || undefined,
        },
      },
    }),
    []
  );
